import { useCallback, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, DrivePickerModal, Input } from '@aimsos/ui';

import { fetchDriveRoot, fetchFolderChildren } from '../../../../services/drives.js';
import { useDrives } from '../../hooks/useDrives.js';

import { FileText, Link2, Paperclip, Plus, X } from 'lucide-react';

/**
 * Files (from the drive picker, as notes do — CT-5528) and URLs attached to a
 * logged call or meeting (CT-5724). Controlled: the form owns the lists.
 */
function InteractionAttachments({ files, urls, disabled, onAddFiles, onRemoveFile, onAddUrl, onRemoveUrl }) {
  const { t } = useTranslation('unified-profiles');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const { drives, loading: drivesLoading } = useDrives({ enabled: pickerOpen });

  const openPicker = useCallback(() => setPickerOpen(true), []);
  const closePicker = useCallback(() => setPickerOpen(false), []);
  const handlePicked = useCallback(
    (items) => {
      onAddFiles(items.map((item) => ({ id: item.document_uuid, name: item.name })));
      setPickerOpen(false);
    },
    [onAddFiles],
  );

  const handleUrlDraft = useCallback((event) => setUrlDraft(event.target.value), []);
  const commitUrl = useCallback(() => {
    const url = urlDraft.trim();
    if (!url) return;
    onAddUrl(url);
    setUrlDraft('');
  }, [onAddUrl, urlDraft]);
  const handleUrlKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      commitUrl();
    },
    [commitUrl],
  );

  return (
    <div className="flex flex-col gap-4">
      <section aria-label={t('history.form.attachments')} className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-content-secondary">{t('history.form.attachments')}</span>
          <Button variant="secondary" size="sm" leftIcon={Paperclip} onClick={openPicker} disabled={disabled}>
            {t('history.form.attach')}
          </Button>
        </div>
        {files.length ? (
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {files.map((file) => (
              <li key={file.id} className="flex items-center justify-between gap-2 rounded-md border border-edge px-3 py-1.5 text-sm">
                <span className="flex min-w-0 items-center gap-2 text-content">
                  <FileText className="size-4 shrink-0 text-content-muted" aria-hidden />
                  <span className="truncate">{file.name}</span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={X}
                  aria-label={t('history.form.removeFile', { name: file.name })}
                  onClick={() => onRemoveFile(file.id)}
                  disabled={disabled}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <section aria-label={t('history.form.urls')} className="flex flex-col gap-2">
        <span className="text-sm font-medium text-content-secondary">{t('history.form.urls')}</span>
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <Input
              aria-label={t('history.form.urlInput')}
              placeholder={t('history.form.urlPlaceholder')}
              value={urlDraft}
              onChange={handleUrlDraft}
              onKeyDown={handleUrlKeyDown}
              disabled={disabled}
            />
          </div>
          <Button variant="secondary" size="md" leftIcon={Plus} onClick={commitUrl} disabled={disabled || !urlDraft.trim()}>
            {t('history.form.addUrl')}
          </Button>
        </div>
        {urls.length ? (
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {urls.map((url) => (
              <li key={url} className="flex items-center justify-between gap-2 rounded-md border border-edge px-3 py-1.5 text-sm">
                <span className="flex min-w-0 items-center gap-2 text-content">
                  <Link2 className="size-4 shrink-0 text-content-muted" aria-hidden />
                  <span className="truncate">{url}</span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={X}
                  aria-label={t('history.form.removeUrl', { url })}
                  onClick={() => onRemoveUrl(url)}
                  disabled={disabled}
                />
              </li>
            ))}
          </ul>
        ) : null}
        <p className="m-0 text-xs text-content-muted">{t('history.form.linksHint')}</p>
      </section>

      <DrivePickerModal
        isOpen={pickerOpen}
        onClose={closePicker}
        onConfirm={handlePicked}
        drives={drives}
        isLoadingDrives={drivesLoading}
        fetchDriveRoot={fetchDriveRoot}
        fetchFolderChildren={fetchFolderChildren}
        title={t('history.form.pickerTitle')}
        cancelLabel={t('history.form.cancel')}
        addSelectedLabel={t('history.form.pickerAdd')}
        useBodyPortal
      />
    </div>
  );
}

export default InteractionAttachments;
